import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../../../common/navbar';
import JobItem from '../dashBoard/jobItem';
import { FiX, FiSearch, FiCheck } from 'react-icons/fi';
import { RecruiterApi } from '../../../supports/recruiterApi';
import Loader from "../../../common/loader";
import unifiedAuthManager from '../../../services/unifiedAuthManager';
import { generateRequestId } from '../../../common/common';
import { resumeUpload } from '../../../supports/resumeUpload';
import DateRangePicker from './floatingLabelDateInput';
import { Calendar } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { setSearchFilters, clearSearchFilters } from "../../../redux/searchFiltersSlice"; // adjust path

const PAGE_SIZE = 10;

const FilterIcon = (props) => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
    >
        <path
            d="M21 6H19M21 12H16M21 18H16M7 20V13.5612C7 13.3532 7 13.2492 6.97958 13.1497C6.96147 13.0615 6.93151 12.9761 6.89052 12.8958C6.84431 12.8054 6.77934 12.7242 6.64939 12.5617L3.35061 8.43826C3.22066 8.27583 3.15569 8.19461 3.10948 8.10417C3.06849 8.02393 3.03853 7.93852 3.02042 7.85026C3 7.75078 3 7.64677 3 7.43875V5.6C3 5.03995 3 4.75992 3.10899 4.54601C3.20487 4.35785 3.35785 4.20487 3.54601 4.10899C3.75992 4 4.03995 4 4.6 4H13.4C13.9601 4 14.2401 4 14.454 4.10899C14.6422 4.20487 14.7951 4.35785 14.891 4.54601C15 4.75992 15 5.03995 15 5.6V7.43875C15 7.64677 15 7.75078 14.9796 7.85026C14.9615 7.93852 14.9315 8.02393 14.8905 8.10417C14.8443 8.19461 14.7793 8.27583 14.6494 8.43826L11.3506 12.5617C11.2207 12.7242 11.1557 12.8054 11.1095 12.8958C11.0685 12.9761 11.0385 13.0615 11.0204 13.1497C11 13.2492 11 13.3532 11 13.5612V17L7 20Z"
            stroke="#004645"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const FilterTab = ({ label, count, isActive, onClick, disabled }) => {
    const countStr = String(count ?? "");
    const countWidth = Math.max(20, 8 * countStr.length + 12);

    return (
        <button
            onClick={onClick}
            className={`relative px-2 pb-1 transition-colors duration-150`}
            disabled={disabled}
            style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
            <span className={`font-semibold text-sm ${isActive ? 'mph-primary' : 'text-gray-999'}`}>{label}</span>
            {isActive && (
                <>
                    <span
                        className={`ml-2 text-xs font-bold mph-primary rounded rounded-xl bg-gray-100`}
                        style={{
                            display: 'inline-block',
                            minWidth: countWidth,
                            textAlign: 'center',
                            padding: '0 6px'
                        }}
                    >
                        {count}
                    </span>
                    <div className="absolute bottom-0 left-0 w-full h-0.5 border border-b border-emerald-800 rounded-full"></div>
                </>
            )}
        </button>
    );
};

const JobsPage = () => {
    const dispatch = useDispatch();
    const persistedFilters = useSelector((state) => state.searchFilters);
    const [activeTab, setActiveTab] = useState('All Jobs');
    const [jobList, setJobList] = useState([]);
    const [searchTerm, setSearchTerm] = useState(persistedFilters.searchTerm || '');
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tabCounts, setTabCounts] = useState({
        'All Jobs': 0,
        'My Position': 0
    });
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const user = unifiedAuthManager.getUnifiedUser();
    const recruiterId = user?.id;
    const loadingRef = useRef(false);
    const abortControllerRef = useRef(null);
    const [searchTotalCount, setSearchTotalCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const location = useLocation();
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(persistedFilters.statusNames || []);
    const [dynamicStatuses, setDynamicStatuses] = useState([]);
    const [showCalendar, setShowCalendar] = useState(false);
    const STATUS_OPTIONS = dynamicStatuses;
    const [startDate, setStartDate] = useState(persistedFilters.startDate || "");
    const [endDate, setEndDate] = useState(persistedFilters.endDate || "");
    const skipInitialLoad = useRef(false);
    const [searchPage, setSearchPage] = useState(0);
    const [lastSearchParams, setLastSearchParams] = useState(null);

    const hasActiveFilters = useCallback(() => {
        return (
            searchTerm.trim() !== "" ||
            selectedStatus.length > 0 ||
            !!startDate ||
            !!endDate
        );
    }, [searchTerm, selectedStatus, startDate, endDate]);

    useEffect(() => {
        setPage(0);
        setHasMore(true);
        setInitialLoad(true);
        setSearchTotalCount(0);
        setTotalCount(0);
    }, [activeTab, recruiterId]);

    useEffect(() => {
        setFilteredJobs(jobList);
    }, [jobList]);

    useEffect(() => {
        async function fetchStatusList() {
            try {
                const response = await RecruiterApi.getJobStatus({
                    recruiter_id: recruiterId,
                    user_payload: {
                        request_id: generateRequestId()
                    },
                });
                if (response.success && Array.isArray(response.data)) {
                    setDynamicStatuses(response.data);
                }
            } catch (err) {
                console.error("Failed to load statuses:", err);
            }
        }
        fetchStatusList();
    }, [recruiterId]);

    useEffect(() => {
        if (skipInitialLoad.current) return;
        if (!recruiterId) return;

        const hasFilters =
            (persistedFilters.searchTerm && persistedFilters.searchTerm.trim() !== "") ||
            (Array.isArray(persistedFilters.statusNames) && persistedFilters.statusNames.length > 0) ||
            persistedFilters.startDate ||
            persistedFilters.endDate;

        console.log(persistedFilters.statusValues)
        if (hasFilters) {
            setJobList([]);
            setPage(0);
            setHasMore(false);
            runJobSearch({
                searchText: persistedFilters.searchTerm || "",
                selectedStatuses: persistedFilters.statusValues || [],
                startDate: persistedFilters.startDate || "",
                endDate: persistedFilters.endDate || ""
            }, 0, true);
        } else {
            setActiveTab(persistedFilters.activeTabJobsPage || "All Jobs");
            setJobList([]);
            setPage(0);
            setHasMore(true);
            fetchJobsByTab(
                persistedFilters.activeTabJobsPage || "All Jobs",
                0,
                PAGE_SIZE,
                true
            );
        }
    }, []);

    const listContainerRef = useRef(null);

    const fetchJobsByTab = async (tabLabel, pageNum, count, isInitial = false) => {
        if (loadingRef.current) return;
        if (!recruiterId) return;

        loadingRef.current = true;
        setLoading(true);
        setError(null);

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const requestBody = {
                recruiter_id: recruiterId,
                user_payload: {
                    request_id: generateRequestId()
                },
            };
            let response;
            if (tabLabel === 'All Jobs') {
                response = await resumeUpload.fetchAlljobs({ ...requestBody, recruiter:true, page: pageNum + 1, limit: count }, { signal: controller.signal });
            } else if (tabLabel === 'My Position') {
                response = await RecruiterApi.getJobsList({ ...requestBody, page: pageNum, count }, { signal: controller.signal });
            }
            if (response?.success) {
                let newJobs;
                setTotalCount(response.data.total_count || response.data.total_jobs);
                newJobs = response.data.jobs || [];
                if ('have_more' in response.data) {
                    setHasMore(response.data.have_more);
                } else {
                    setHasMore(isInitial ? newJobs.length < response.data.total_count : (jobList + newJobs).length < response.data.total_count);
                }
                if (isInitial) {
                    setJobList(newJobs);
                } else {
                    setJobList(prev => [...prev, ...newJobs]);
                }
                setTabCounts(prev => ({ ...prev, [tabLabel]: response.data.total_count || response.data.total_jobs || newJobs.length }));
            } else {
                if (isInitial) setJobList([]);
                setHasMore(false);
                setTabCounts(prev => ({ ...prev, [tabLabel]: 0 }));
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                setError("Failed to load jobs. Please try again.");
            }
            setHasMore(false);
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    };

    const runJobSearch = async (params, pageNum = 0, isInitial = true) => {
        const { searchText = "", selectedStatuses = [], startDate = "", endDate = "" } = params;
        if (loading) return;
        setLoading(true);
        setError(null);
        if (isInitial) {
            setInitialLoad(true);
            setSearchPage(0);
            setLastSearchParams(params);
            setJobList([]);
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        let statusValues = [];
        if (selectedStatuses.length > 0) {
            if (typeof selectedStatuses[0] === 'number') {
                // already values
                statusValues = selectedStatuses;
            } else {
                // names
                if (dynamicStatuses.length > 0) {
                    statusValues = dynamicStatuses
                        .filter((s) => selectedStatuses.includes(s.name))
                        .map((s) => s.value);
                } else {
                    statusValues = selectedStatuses; // fallback, though unlikely
                }
            }
        }
        try {
            const body = {
                user_payload: { request_id: generateRequestId() },
                page: pageNum ,
                limit: PAGE_SIZE
            };

            if (searchText) body.search_text = searchText;

            if (statusValues.length > 0) {
                body.job_status = statusValues;
            }
            if (startDate) body.start_date = startDate;
            if (endDate) body.end_date = endDate;
            if(activeTab === "My Position") {
                body.recruiter_id = recruiterId;
            }

            const response = await RecruiterApi.getJobsBySearch(body, { signal: controller.signal });

            if (response.success) {
                const jobs = response.data.job_descriptions || [];
                setSearchTotalCount(response.data.total_count || 0);
                if (isInitial) {
                    setJobList(jobs);
                } else {
                    setJobList(prev => [...prev, ...jobs]);
                }
                setTabCounts((prev) => ({
                    ...prev,
                    [activeTab]: response.data.total_count || jobs.length,
                }));
                setHasMore(jobs.length === PAGE_SIZE && (isInitial ? jobs.length : (jobList.length + jobs.length)) < (response.data.total_count || 0));
            } else {
                if (isInitial) setJobList([]);
                setTabCounts((prev) => ({ ...prev, [activeTab]: 0 }));
                setHasMore(false);
            }
        } catch (error) {
            if (error.name !== "AbortError") {
                console.error(error);
                setError("Error fetching jobs");
                setJobList([]);
                setHasMore(false);
            }
        } finally {
            setLoading(false);
            if (isInitial) {
                dispatch(clearSearchFilters());
            }
        }
    };

    useEffect(() => {
        if (skipInitialLoad.current) return;
        if (!recruiterId) return;
        // if (hasActiveFilters()) {
        //     return;
        // }
        fetchJobsByTab(activeTab, 0, PAGE_SIZE, true);
    }, [activeTab, recruiterId,]);

    const handleScroll = useCallback(() => {
        if (loadingRef.current || !listContainerRef.current) return;
        const container = listContainerRef.current;
        const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;

        if (nearBottom && hasMore && !loading) {
            if (hasActiveFilters()) {
                // Load more for search
                const nextPage = searchPage + 1;
                setSearchPage(nextPage);
                setInitialLoad(false);
                runJobSearch(lastSearchParams, nextPage, false);
            } else {
                // Load more for tab
                const nextPage = page + 1;
                setPage(nextPage);
                setInitialLoad(false);
                fetchJobsByTab(activeTab, nextPage, PAGE_SIZE, false);
            }
        }
    }, [hasActiveFilters, hasMore, page, activeTab, searchPage, lastSearchParams, loading]);

    useEffect(() => {
        const container = listContainerRef.current;
        if (!container) return;
        container.addEventListener('scroll', handleScroll);
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    const handleTabClick = (tabLabel) => {
        if (loading) return;
        if (activeTab === tabLabel) return;
        setActiveTab(tabLabel);
        dispatch(clearSearchFilters()); 
        setSearchTerm('');
        setSelectedStatus([]);
        setStartDate('');
        setEndDate('');

        setTabCounts((prev) => ({
            ...prev,
            [tabLabel]: jobList.length,
        }));
        setPage(0);
        setHasMore(true);
        setInitialLoad(true);
    };

    const handleSearchButtonClick = (e) => {
        e.preventDefault();
        const trimmed = searchTerm.trim();
        const encodedTerm = encodeURIComponent(trimmed);
        if (encodedTerm) {
            window.history.pushState({}, '', `?q=${encodedTerm}`);
        } else {
            window.history.replaceState({}, "", window.location.pathname);
        }

        const upcomingHasFilters =
            trimmed !== "" || selectedStatus.length > 0 || !!startDate || !!endDate;

        if (upcomingHasFilters) {
            runJobSearch({
                searchText: trimmed,
                selectedStatuses: selectedStatus,
                startDate,
                endDate
            }, 0, true);
        } else {
            setJobList([]);
            setPage(0);
            setHasMore(true);
            setInitialLoad(true);
            fetchJobsByTab(activeTab, 0, PAGE_SIZE, true);
        }
    };

    const handleSearchInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = searchTerm.trim();
            const upcomingHasFilters =
                trimmed !== "" || selectedStatus.length > 0 || !!startDate || !!endDate;

            if (upcomingHasFilters) {
                runJobSearch({
                    searchText: trimmed,
                    selectedStatuses: selectedStatus,
                    startDate,
                    endDate
                }, 0, true);
            } else {
                setJobList([]);
                setPage(0);
                setHasMore(true);
                setInitialLoad(true);
                fetchJobsByTab(activeTab, 0, PAGE_SIZE, true);
            }
        }
    };

    const handleClearSearch = () => {
        const hadSearch = searchTerm.trim() !== "";
        setSearchTerm('');
        window.history.replaceState({}, "", window.location.pathname);

        const otherFiltersExist =
            selectedStatus.length > 0 || !!startDate || !!endDate;

        if (otherFiltersExist) {
            runJobSearch({
                searchText: "",
                selectedStatuses: selectedStatus,
                startDate,
                endDate
            }, 0, true);
        } else if (hadSearch) {
            dispatch(clearSearchFilters());
            setJobList([]);
            setPage(0);
            setHasMore(true);
            setInitialLoad(true);
            fetchJobsByTab(activeTab, 0, PAGE_SIZE, true);
        }
    };

    const getNoJobsMessage = (tab) => {
        if (hasActiveFilters()) {
            return "No jobs found matching criteria";
        }
        switch (tab) {
            case 'Active': return "There are no active jobs.";
            case 'Closed': return "There are no closed jobs.";
            default: return "There are no jobs.";
        }
    };

    const tabs = [
        { label: 'All Jobs', count: tabCounts['All Jobs'] },
        { label: 'My Position', count: tabCounts['My Position'] }
    ];

    const getStatusOptionsForTab = () => STATUS_OPTIONS;

    const toggleStatus = (statusName) => {
        let newSelectedStatus;

        if (selectedStatus.includes(statusName)) {
            newSelectedStatus = selectedStatus.filter(s => s !== statusName);
        } else {
            newSelectedStatus = [...selectedStatus, statusName];
        }

        setSelectedStatus(newSelectedStatus);

        const trimmed = searchTerm.trim();
        const upcomingHasFilters =
            trimmed !== "" || newSelectedStatus.length > 0 || !!startDate || !!endDate;

        if (upcomingHasFilters) {
            runJobSearch({
                searchText: trimmed,
                selectedStatuses: newSelectedStatus,
                startDate,
                endDate
            }, 0, true);
        } else {
            setJobList([]);
            setPage(0);
            setHasMore(true);
            setInitialLoad(true);
            fetchJobsByTab(activeTab, 0, PAGE_SIZE, true);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        const prevTrimmed = searchTerm.trim();
        setSearchTerm(value);

        if (value.trim() === '' && !loading && prevTrimmed !== '') {
            handleClearSearch();
        }
    };

    const selectAllStatuses = () => {
        const trimmed = searchTerm.trim();
        setSelectedStatus([]);

        const upcomingHasFilters =
            trimmed !== "" || !!startDate || !!endDate;

        if (upcomingHasFilters) {
            runJobSearch({
                searchText: trimmed,
                selectedStatuses: [],
                startDate,
                endDate
            }, 0, true);
        } else {
            dispatch(clearSearchFilters());
            setJobList([]);
            setPage(0);
            setHasMore(true);
            setInitialLoad(true);
            fetchJobsByTab(activeTab, 0, PAGE_SIZE, true);
        }
    };

    const isStatusSelected = (status) => selectedStatus.includes(status);
    const isAllSelected = () => selectedStatus.length === 0;

    const showNoMoreJobs = !hasMore && !loading && filteredJobs.length > 0;

    const calendarRef = useRef(null);
    const statusDropDownRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
            if (statusDropDownRef.current && !statusDropDownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return "";

        const [y, m, d] = dateStr.split("-");
        const dateObj = new Date(y, m - 1, d);

        const day = String(dateObj.getDate()).padStart(2, "0");
        const month = dateObj.toLocaleString("en-US", { month: "short" });
        const year = dateObj.getFullYear();

        return `${day} ${month} ${year}`;
    };

    useEffect(() => {
    return () => {
        // Clear filters when component unmounts (e.g., on logout)
        dispatch(clearSearchFilters());
    };
}, [dispatch]);

    return (
        <div className="min-h-screen main-background">
            <Navbar />
            <main className="mx-auto px-8 pb-8 pt-28 anim-slideUp">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-8 flex flex-col max-h-[calc(100vh-8rem)]">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-6">
                            {tabs.map(tab => (
                                <FilterTab
                                    key={tab.label}
                                    label={tab.label}
                                    count={tab.count}
                                    isActive={activeTab === tab.label}
                                    onClick={() => handleTabClick(tab.label)}
                                    disabled={loading}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="relative mt-6 flex items-center">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                value={searchTerm}
                                maxLength={250}
                                onChange={handleInputChange}
                                onKeyDown={handleSearchInputKeyDown}
                                placeholder="Search using Job Name or Job ID"
                                className="w-full border rounded-lg py-3 pl-4 pr-12 text-xs focus:ring-0 text-gray-333"
                                disabled={loading}
                            />
                            <button
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient text-white h-7 w-7 flex items-center justify-center rounded-full transition-transform duration-200 hover:scale-110 hover:shadow-lg"
                                onClick={handleSearchButtonClick}
                                disabled={loading}
                                style={{ zIndex: 2 }}
                            >
                                <FiSearch size={18} />
                            </button>
                            {searchTerm && (
                                <button
                                    className="absolute right-10 top-1/2 -translate-y-1/2 bg-gray-200 text-gray-333 h-6 w-6 flex items-center justify-center rounded-full hover:bg-gray-300 transition-colors"
                                    onClick={handleClearSearch}
                                    disabled={loading}
                                    style={{ zIndex: 3 }}
                                    aria-label="Clear search"
                                    type="button"
                                >
                                    <FiX size={16} />
                                </button>
                            )}
                        </div>
                        <div className="relative ml-2">
                            <button
                                type="button"
                                aria-label="Filter"
                                className="flex items-center justify-center hover:bg-gray-200 rounded-full p-1 border border-gray-200 relative"
                                style={{ minWidth: 36, minHeight: 36 }}
                                tabIndex={0}
                                onClick={() => setShowStatusDropdown((prev) => !prev)}
                            >
                                <FilterIcon />
                                {!isAllSelected() && selectedStatus.length > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                                        {selectedStatus.length}
                                    </span>
                                )}
                            </button>
                            {showStatusDropdown && (
                                <div ref={statusDropDownRef}
                                    className="absolute overflow-y-auto z-20 right-0 top-12 bg-white border border-gray-200 rounded shadow-md min-w-[180px] py-2"
                                    style={{ maxHeight: '362px' }}
                                >
                                    {(() => {
                                        const statusOptions = getStatusOptionsForTab(activeTab);
                                        if (Array.isArray(statusOptions) && statusOptions.length > 0) {
                                            return (
                                                <>
                                                    <button
                                                        disabled={loading}
                                                        key="all"
                                                        type="button"
                                                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${isAllSelected() ? 'bg-gray-100 font-semibold mph-primary' : 'text-gray-666'
                                                            }`}
                                                        onClick={selectAllStatuses}
                                                    >
                                                        <span>All</span>
                                                        {isAllSelected() && <FiCheck className="mph-primary" />}
                                                    </button>
                                                    <div className="border-t border-gray-200 my-1"></div>
                                                    {statusOptions.map((statusObj) => (
                                                        <button
                                                            disabled={loading}
                                                            key={statusObj.value}
                                                            type="button"
                                                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${isStatusSelected(statusObj.name)
                                                                ? "bg-gray-100 font-semibold mph-primary"
                                                                : "text-gray-666"
                                                                }`}
                                                            onClick={() => { toggleStatus(statusObj.name); }}
                                                        >
                                                            <span>{statusObj.name}</span>
                                                            {isStatusSelected(statusObj.name) && <FiCheck className="mph-primary" />}
                                                        </button>
                                                    ))}

                                                </>
                                            );
                                        } else {
                                            return (
                                                <div className="px-4 py-2 text-gray-400 text-sm">No statuses</div>
                                            );
                                        }
                                    })()}
                                </div>
                            )}
                        </div>
                        <div className="relative ml-2 flex items-center gap-4">
                            <div className="relative">
                                <button
                                    disabled={loading}
                                    type="button"
                                    onClick={() => setShowCalendar((prev) => !prev)}
                                    className="px-3 py-2 border rounded-lg hover:bg-gray-100 flex items-center gap-2 text-xs"
                                >
                                    <Calendar size={16} className="mph-primary" />

                                    {startDate && endDate ? (
                                        <span>{formatDisplayDate(startDate)} → {formatDisplayDate(endDate)}</span>
                                    ) : startDate ? (
                                        <span>{formatDisplayDate(startDate)}</span>
                                    ) : (
                                        <span className="text-gray-400">Select dates</span>
                                    )}
                                </button>
                                {showCalendar ? (
                                    <div ref={calendarRef} className="absolute right-0 mt-2 z-30">
                                        <DateRangePicker
                                            selectedStart={startDate}
                                            selectedEnd={endDate}
                                            onChange={({ startDate: s, endDate: e }) => {
                                                console.log(s, e)
                                                setShowCalendar(false);
                                                setStartDate(s);
                                                setEndDate(e);
                                                const finalEnd = e ?? s;

                                                const trimmed = searchTerm.trim();
                                                const upcomingHasFilters =
                                                    trimmed !== "" ||
                                                    selectedStatus.length > 0 ||
                                                    !!s ||
                                                    !!finalEnd;

                                                if (upcomingHasFilters) {
                                                    runJobSearch({
                                                        searchText: trimmed,
                                                        selectedStatuses: selectedStatus,
                                                        startDate: s,
                                                        endDate: finalEnd
                                                    }, 0, true);
                                                } else {
                                                    setJobList([]);
                                                    setPage(0);
                                                    setHasMore(true);
                                                    setInitialLoad(true);
                                                    fetchJobsByTab(activeTab, 0, PAGE_SIZE, true);
                                                }
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div
                        className="mt-8 space-y-4 flex-1 overflow-y-auto pr-2"
                        ref={listContainerRef}
                        style={{ minHeight: 200 }}
                    >
                        {loading && initialLoad ? (
                            <Loader message="Loading jobs" />
                        ) : filteredJobs.length === 0 ? (
                            <div className="text-gray-666 items-center flex text-sm mt-8 justify-center">
                                {getNoJobsMessage(activeTab)}
                            </div>
                        ) : (
                            <>
                                {filteredJobs.map((job, idx) => (
                                    <JobItem key={`${job?.id}-${idx}`} job={job} searchTerm={searchTerm} selectedStatus={selectedStatus} startDate={startDate} endDate={endDate} activeTab={activeTab} dynamicStatuses={dynamicStatuses} />
                                ))}
                                {(loading && !initialLoad) ? (
                                    <div className="flex justify-center py-4">
                                        <Loader message="Loading more jobs" />
                                    </div>
                                ) : null}
                                {showNoMoreJobs && (hasActiveFilters() ? searchPage > 0 : page > 0) && (
                                    <div className="text-center text-xs text-gray-500 py-2">
                                        No more jobs to load.
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                </div>
            </main>
        </div>
    );
};

export default JobsPage;